const SESSION_KEY = "jourfx_appointment_session";

export interface AppointmentSession {
  appointmentId: string;
  validatedAt: number;
  expiresAt: number;
  sessionToken: string;
}

function normalizeAppointmentId(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toUnixMs(value: unknown): number | null {
  const normalizeEpoch = (parsed: number): number => {
    if (parsed > 0 && parsed < 1_000_000_000_000) {
      return parsed * 1000;
    }
    return parsed;
  };

  if (typeof value === "number" && Number.isFinite(value)) {
    return normalizeEpoch(value);
  }
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return normalizeEpoch(parsed);

    const parsedDate = Date.parse(value);
    return Number.isFinite(parsedDate) ? parsedDate : null;
  }
  return null;
}

export function getSession(): AppointmentSession | null {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    const appointmentId = normalizeAppointmentId(parsed.appointmentId);
    const validatedAt = toUnixMs(parsed.validatedAt);
    const expiresAt = toUnixMs(parsed.expiresAt);

    if (
      appointmentId === null ||
      typeof parsed.sessionToken !== "string" ||
      validatedAt === null ||
      expiresAt === null
    ) {
      clearSession();
      return null;
    }

    const session: AppointmentSession = {
      ...parsed,
      appointmentId,
      validatedAt,
      expiresAt,
    };
    if (session.expiresAt < Date.now()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function setSession(session: AppointmentSession): void {
  const appointmentId = normalizeAppointmentId(session.appointmentId);
  if (appointmentId === null) {
    clearSession();
    return;
  }

  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      ...session,
      appointmentId,
    })
  );
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

export function isSessionValid(appointmentId: string): boolean {
  const session = getSession();
  const targetId = normalizeAppointmentId(appointmentId);
  return (
    session !== null &&
    targetId !== null &&
    session.appointmentId === targetId &&
    session.expiresAt >= Date.now()
  );
}
